import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { EMPTY_NETWORK_DIRECTOR_LIST_CONFIGURATION, EMPTY_NETWORK_DIRECTOR_LIST_CRITERIA } from "../constant/networkDirectorConstant";
import { NetworkDirectorState } from "../objects/networkDirectorState";
import { Preferences } from "../../profile/objects/preferences";
import { setNetworkDirectorPreference } from "../utils/setNetworkDirectorPreference";
import { NetworkDirectorListCriteria } from "../objects/networkDirectorListCriteria";
import { NetworkDirector } from "../objects/networkDirector";
import { Column } from "../objects/column";
import { cloneDeep } from "lodash";

const initialState: NetworkDirectorState = {
    network_director_list: [],
    network_director_list_configuration: EMPTY_NETWORK_DIRECTOR_LIST_CONFIGURATION,
    network_director_list_loading: false,
    network_director_list_count: 0,
    network_director_list_criteria: EMPTY_NETWORK_DIRECTOR_LIST_CRITERIA,
    network_director: undefined
};
export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setPreferenceReducer = (state: NetworkDirectorState, action: PayloadAction<Preferences[]>): NetworkDirectorState => {
    const { preferences, columns, order_by, order } = setNetworkDirectorPreference(action.payload, state.network_director_list_configuration.columns);
    return {
        ...state,
        network_director_list_configuration: { ...state.network_director_list_configuration, preferences, columns },
        network_director_list_criteria: { ...state.network_director_list_criteria, order, order_by },
    };
};
const addNetworkDirectorListConfigurationPreferenceReducer = (state: NetworkDirectorState, action: PayloadAction<Preferences[]>): NetworkDirectorState => ({
    ...state, network_director_list_configuration: { ...state.network_director_list_configuration, preferences: [...state.network_director_list_configuration.preferences, ...action.payload] }
});
const setNetworkDirectorListConfigurationPreferenceReducer = (state: NetworkDirectorState, action: PayloadAction<Preferences[]>): NetworkDirectorState => ({
    ...state, network_director_list_configuration: { ...state.network_director_list_configuration, preferences: action.payload }
});
const setColumnsReducer = (state: NetworkDirectorState, action: PayloadAction<Column[]>): NetworkDirectorState => ({
    ...state, network_director_list_configuration: { ...state.network_director_list_configuration, columns: action.payload },
});
const toggleColumnReducer = (state: NetworkDirectorState, action: PayloadAction<number>): NetworkDirectorState => {
    const columns = cloneDeep([...state.network_director_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, network_director_list_configuration: { ...state.network_director_list_configuration, columns } };
};
const updateNetworkDirectorReducer = (state: NetworkDirectorState, action: PayloadAction<NetworkDirector | undefined>): NetworkDirectorState => ({
    ...state, network_director: action.payload
});
const updateNetworkDirectorListReducer = (state: NetworkDirectorState, action: PayloadAction<NetworkDirector[]>): NetworkDirectorState => ({
    ...state, network_director_list: action.payload
});
const updateNetworkDirectorListCriteriaReducer = (state: NetworkDirectorState, action: PayloadAction<NetworkDirectorListCriteria>): NetworkDirectorState => ({
    ...state, network_director_list_criteria: action.payload
});
const updateNetworkDirectorListCountReducer = (state: NetworkDirectorState, action: PayloadAction<number>): NetworkDirectorState => ({
    ...state, network_director_list_count: action.payload
});
const updateNetworkDirectorListLoadingReducer = (state: NetworkDirectorState, action: PayloadAction<boolean>): NetworkDirectorState => ({
    ...state, network_director_list_loading: action.payload
});

const resizeColumnReducer = (state: NetworkDirectorState, action: PayloadAction<ColumnResizePayload>): void => {
    state.network_director_list_configuration.columns = applyColumnResize(state.network_director_list_configuration.columns, action.payload);
};

export const network_director = createSlice({
    name: 'network_director',
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    },
    reducers: {
        addNetworkDirectorListConfigurationPreference: addNetworkDirectorListConfigurationPreferenceReducer,
        setNetworkDirectorListConfigurationPreference: setNetworkDirectorListConfigurationPreferenceReducer,
        setColumns: setColumnsReducer,
        toggleColumn: toggleColumnReducer,
        updateNetworkDirector: updateNetworkDirectorReducer,
        updateNetworkDirectorList: updateNetworkDirectorListReducer,
        updateNetworkDirectorListCount: updateNetworkDirectorListCountReducer,
        updateNetworkDirectorListCriteria: updateNetworkDirectorListCriteriaReducer,
        updateNetworkDirectorListLoading: updateNetworkDirectorListLoadingReducer,
        resizeColumn: resizeColumnReducer
    }
});

export const {
    addNetworkDirectorListConfigurationPreference,
    setColumns,
    toggleColumn,
    setNetworkDirectorListConfigurationPreference,
    updateNetworkDirector,
    updateNetworkDirectorList,
    updateNetworkDirectorListCount,
    updateNetworkDirectorListCriteria,
    updateNetworkDirectorListLoading,
    resizeColumn
} = network_director.actions;
